import { X } from 'lucide-react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ConfigSummary } from '../config';
import ApiEndpointSelector from './ApiEndpointSelector';
import ClientSelector from './ClientSelector';
import ConfigurationTab from './ConfigurationTab';
import FeaturesTab from './FeaturesTab';

interface ConfigPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onConfigChange: () => void;

    // Status
    apiHealthStatus: Record<
        string,
        { status: 'checking' | 'online' | 'offline'; version?: string }
    >;
    configSummary: ConfigSummary;

    // Health refresh
    onRefreshHealthChecks: () => void;
    isRefreshingHealth: boolean;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
    isOpen,
    onClose,
    onConfigChange,

    // Status
    apiHealthStatus,
    configSummary,

    // Health refresh
    onRefreshHealthChecks,
    isRefreshingHealth,
}) => {
    if (!isOpen) return null;

    return (
        <div className="config-panel-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-6">
            <div
                className="config-panel-container bg-white rounded-xl w-[800px] max-w-full h-[600px] flex flex-col overflow-hidden shadow-2xl"
                data-ph-capture-attribute-panel-type="config-settings"
            >
                {/* Header */}
                <div className="config-panel-header px-4 py-3 border-b flex-shrink-0">
                    <div className="config-panel-title-row flex justify-between items-center mb-2">
                        <h2 className="config-panel-title m-0 text-lg font-semibold text-gray-800">
                            InboundX Test Settings
                        </h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="config-panel-close-button w-7 h-7 rounded-full hover:bg-gray-100"
                            data-ph-capture-attribute-button-type="close"
                            data-ph-capture-attribute-panel="config-settings"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <Tabs
                    defaultValue="server-client"
                    className="config-panel-tabs w-full flex-1 min-h-0 flex flex-col"
                >
                    <TabsList className="config-panel-tabs-list grid w-full grid-cols-3 h-8 flex-shrink-0">
                        <TabsTrigger
                            value="server-client"
                            className="config-tab-server-client flex items-center gap-1 text-xs py-1"
                            data-ph-capture-attribute-tab="server-client"
                        >
                            <span>🌐</span>
                            Server & Client
                        </TabsTrigger>
                        <TabsTrigger
                            value="config"
                            className="config-tab-configuration flex items-center gap-1 text-xs py-1"
                            data-ph-capture-attribute-tab="configuration"
                        >
                            <span>🔧</span>
                            Configuration
                        </TabsTrigger>
                        <TabsTrigger
                            value="features"
                            className="config-tab-features flex items-center gap-1 text-xs py-1"
                            data-ph-capture-attribute-tab="features"
                        >
                            <span>🧪</span>
                            Features
                        </TabsTrigger>
                    </TabsList>

                    {/* Content */}
                    <div className="config-panel-content flex-1 min-h-0 overflow-y-auto p-3">
                        {/* Combined Server & Client Tab */}
                        <TabsContent
                            value="server-client"
                            className="config-tab-content-server-client m-0 h-full overflow-y-auto"
                        >
                            {/* API Endpoint Selection */}
                            <ApiEndpointSelector
                                apiHealthStatus={apiHealthStatus}
                                onRefreshHealthChecks={onRefreshHealthChecks}
                                isRefreshingHealth={isRefreshingHealth}
                            />

                            {/* Client Selection */}
                            <ClientSelector onConfigChange={onConfigChange} />
                        </TabsContent>

                        {/* Configuration Tab */}
                        <TabsContent value="config" className="config-tab-content-configuration">
                            <ConfigurationTab
                                configSummary={configSummary}
                                onConfigChange={onConfigChange}
                            />
                        </TabsContent>

                        {/* Features Tab */}
                        <TabsContent value="features" className="config-tab-content-features">
                            <FeaturesTab onConfigChange={onConfigChange} />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </div>
    );
};

export default ConfigPanel;
